import { Ref, computed } from 'vue';
import { updateMultiple } from '@/ui/queryState';
import { DateRange, PaginationResetCallback } from '@/ui/types';
import { dateRangeField } from '@/ui/components/form/DateRangeField';
import { useTimeZonedDates } from '@/ui/concerns/useTimeZonedDates';

interface DateRangeFilterHelperProps {
  start: Ref<Date | null>;
  end: Ref<Date | null>;
  name: string;
  label: string;
  placeholder: string;
  clearable: boolean;
  reset: PaginationResetCallback;
  rangeLimitMonths?: number;
  hideLabel?: boolean;
  horizontal?: boolean;
}

const { getDateInLocalTimeZone, getDateInUserTimeZone } = useTimeZonedDates();

export const dateRangeInputCallback = (
  start: Ref<Date | null>,
  end: Ref<Date | null>,
  reset: PaginationResetCallback,
) => ([startValue, endValue]: DateRange) => updateMultiple(() => {
  reset();

  //can be null when date range reset
  if (!startValue && !endValue) {
    start.value = null;
    end.value = null;

    return;
  }

  const newEndValue = new Date(endValue);
  newEndValue.setDate(newEndValue.getDate() + 1);
  newEndValue.setTime(newEndValue.getTime() - 1);

  start.value = getDateInLocalTimeZone(startValue);
  end.value = getDateInLocalTimeZone(newEndValue);
}, true);

export const dateRangeFilter = ({
  start,
  end,
  name,
  label,
  placeholder,
  clearable,
  reset,
  rangeLimitMonths,
  hideLabel,
  horizontal,
}: DateRangeFilterHelperProps) => {
  const timezonedStart = computed(() => start.value ? getDateInUserTimeZone(start.value) : null);
  const timezonedEnd = computed(() => end.value ? getDateInUserTimeZone(end.value) : null);

  return dateRangeField(
    {
      value: timezonedStart.value !== null && timezonedEnd.value !== null
        ? [timezonedStart.value, timezonedEnd.value]
        : null,
      name,
      label,
      placeholder,
      clearable,
      required: false,
      errors: [],
      disabled: false,
      rangeLimitMonths,
      hideLabel,
      horizontal,
    },
    dateRangeInputCallback(start, end, reset),
  );
};
